/**
 * Language configuration for multi-language support (Issue #435)
 *
 * Defines supported languages with their voice configurations.
 * Text mode is available in all 70 entries; voice mode is gated by
 * `voiceSupported`, which mirrors the native-audio model allowlist below.
 *
 * References:
 * - Gemini Live API: https://ai.google.dev/gemini-api/docs/live-guide
 * - Chirp 3 HD voices: https://docs.cloud.google.com/text-to-speech/docs/chirp3-hd
 */

/**
 * Native-audio language allowlist for the pinned voice model — currently
 * `gemini-3.1-flash-live-preview` (see frontend/src/lib/voice/constants.ts /
 * resolveVoiceModel.ts; migrated #2131/D-033).
 * Source: https://docs.cloud.google.com/vertex-ai/generative-ai/docs/live-api/configure-language-voice
 *
 * Any entry in SUPPORTED_LANGUAGES whose `voiceLocale` is NOT in this set must
 * have `voiceSupported: false` — otherwise the Live API rejects the session
 * with `1007 Unsupported language code` mid-connect (Issue #1220, Slovenian
 * learner). The drift-guard unit test asserts this invariant.
 *
 * VERIFIED 2026-08-01 (#1831): general Live API docs list ~97 supported
 * languages, but empirical probing of 9 demand-grounded candidates against
 * `gemini-2.5-flash-native-audio-preview-12-2025` found 0/9 passing — all
 * REJECTED with 1007. Results: docs/pipeline-runs/mega74-1831-locale-probe.json.
 *
 * RE-PROBED 2026-08-14 for the #2131 SKU migration: all 9 of those candidates
 * connect cleanly on `gemini-3.1-flash-live-preview` — CONNECTION only, not
 * evidence of quality (see history below). Results:
 * docs/pipeline-runs/1831-locale-reprobe-3.1-2026-08-14.json.
 *
 * QUALITY-PROBED 2026-09-16 (#1831, the decision-bearing half): the earlier
 * "arbitrary output language" finding was an artifact of a probe prompt that
 * never named a target language. Production always does (PromptComposerV3.js
 * `_buildLanguageLine()`), so scripts/voice-1831-quality-probe.mjs reproduces
 * that exact instruction shape. 20/20 locales probed — the 9 candidates
 * above, the 9 Africa-relevant text-only candidates added to the table below
 * at #1837/A3 Phase 1 (yo/ha/rw/so/om/wo/ak/tn/st), and 2 controls (fr-FR,
 * en-US) — connected AND replied, unmistakably, in the target language for
 * one short turn. All 20 are ADDED below. Full method, per-locale transcripts
 * and cost: docs/pipeline-runs/1831-locale-quality-probe-3.1-2026-09-16.json.
 * This does NOT establish sustained multi-turn fluency or real-learner ASR
 * quality — see backend/src/lib/voiceLanguages.js's header for the full
 * caveat and the standing per-language reader that covers that once traffic
 * exists.
 *
 * TODO: revisit on model upgrade — #1220 / #1831. Re-run
 * scripts/voice-1831-demand-probe.mjs then scripts/voice-1831-quality-probe.mjs
 * against the new pin before touching any voiceSupported flag.
 *
 * WITHDRAWN 2026-09-22 (#3142, D-154/D-156): `om-ET`, `wo-SN`, `ak-GH`,
 * `tn-ZA` removed. This set is meant to be `NATIVE_AUDIO_VOICE_LOCALES ∩
 * offered` — those four are Tier C (not on Google's own supported-language
 * list, docs/languages/translation-playbook.md §2) and are withdrawn from
 * every surface, voice included. `st-ZA`/`so-SO` are KEPT — D-156
 * (2026-09-17) decided Sesotho and Somali keep voice; no special case
 * needed, they are simply still `offered` (Tier B) per
 * docs/i18n/language-tiers.json. See WITHDRAWN_LANGUAGE_CODES below, which
 * mirrors the same four codes for the SUPPORTED_LANGUAGES selector filter —
 * a CI drift check (scripts/test/language-tiers-drift.test.mjs) keeps both
 * this set and WITHDRAWN_LANGUAGE_CODES in sync with docs/i18n/
 * language-tiers.json.
 */
export const NATIVE_AUDIO_VOICE_LOCALES: ReadonlySet<string> = new Set([
  'ar-EG', 'bn-BD', 'nl-NL', 'en-IN', 'en-US', 'fr-FR', 'de-DE',
  'hi-IN', 'id-ID', 'it-IT', 'ja-JP', 'ko-KR', 'mr-IN', 'pl-PL',
  'pt-BR', 'ro-RO', 'ru-RU', 'es-US', 'ta-IN', 'te-IN', 'th-TH',
  'tr-TR', 'uk-UA', 'vi-VN',
  // Added 2026-09-16 (#1831 quality probe) — connect + one-turn quality
  // verified against gemini-3.1-flash-live-preview, see header above.
  'sv-SE', 'sw-KE', 'zu-ZA', 'cs-CZ', 'nb-NO', 'da-DK', 'el-GR', 'ar-XA', 'bn-IN',
  'yo-NG', 'ha-NG', 'rw-RW', 'so-SO', 'st-ZA',
]);

/**
 * Withdrawn locale codes (#3142, D-154) — Tier C in
 * docs/languages/translation-playbook.md §2 / docs/i18n/language-tiers.json.
 * A short, hand-mirrored literal (not a JSON import — the frontend bundle
 * has no runtime access to docs/i18n/language-tiers.json, matching the
 * established NATIVE_AUDIO_VOICE_LOCALES mirroring pattern above), kept in
 * sync by scripts/test/language-tiers-drift.test.mjs (CI, extracts this
 * literal by regex the same way backend/src/__tests__/
 * voice-language-allowlist.test.js already does for NATIVE_AUDIO_VOICE_LOCALES).
 *
 * `LANGUAGE_MAP`/`SUPPORTED_LANGUAGES` entries for these codes are NOT
 * removed (#3142 "what must NOT change" — a learner whose run already used
 * one of these locales must still see a real display name), only filtered
 * OUT of `LANGUAGE_GROUPS`, the shared export every selector dropdown
 * renders from.
 */
export const WITHDRAWN_LANGUAGE_CODES: ReadonlySet<string> = new Set(['wo', 'ak', 'om', 'tn']);

export type LanguageConfig = {
  /** Language code (e.g., 'fr', 'es') */
  code: string;
  /** Display name in native language + English */
  name: string;
  /** Whether voice mode is supported */
  voiceSupported: boolean;
  /** BCP-47 voice locale (e.g., 'fr-FR') for Gemini Live API */
  voiceLocale: string | null;
  /** Default voice name from AVAILABLE_VOICES */
  defaultVoice: string;
};

/**
 * Supported languages: text mode for all 70; voice mode only where
 * `voiceLocale` ∈ NATIVE_AUDIO_VOICE_LOCALES (42-locale allowlist above, per
 * the 2026-09-16 #1831 expansion).
 *
 * Note on Spanish: the model expects `es-US` but we keep `voiceLocale: 'es-ES'`
 * here pending partner coordination (#1220 risk note). Flag is `false` until
 * the locale change ships in a follow-up.
 */
export const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  // Primary
  { code: 'en', name: 'English', voiceSupported: true, voiceLocale: 'en-US', defaultVoice: 'Zephyr' },

  // European languages
  { code: 'fr', name: 'Français (French)', voiceSupported: true, voiceLocale: 'fr-FR', defaultVoice: 'Aoede' },
  { code: 'es', name: 'Español (Spanish)', voiceSupported: false, voiceLocale: 'es-ES', defaultVoice: 'Aoede' },
  { code: 'de', name: 'Deutsch (German)', voiceSupported: true, voiceLocale: 'de-DE', defaultVoice: 'Aoede' },
  { code: 'it', name: 'Italiano (Italian)', voiceSupported: true, voiceLocale: 'it-IT', defaultVoice: 'Aoede' },
  { code: 'pt', name: 'Português (Portuguese)', voiceSupported: true, voiceLocale: 'pt-BR', defaultVoice: 'Aoede' },
  { code: 'nl', name: 'Nederlands (Dutch)', voiceSupported: true, voiceLocale: 'nl-NL', defaultVoice: 'Aoede' },
  { code: 'pl', name: 'Polski (Polish)', voiceSupported: true, voiceLocale: 'pl-PL', defaultVoice: 'Aoede' },
  { code: 'ru', name: 'Русский (Russian)', voiceSupported: true, voiceLocale: 'ru-RU', defaultVoice: 'Aoede' },
  { code: 'uk', name: 'Українська (Ukrainian)', voiceSupported: true, voiceLocale: 'uk-UA', defaultVoice: 'Aoede' },
  { code: 'sv', name: 'Svenska (Swedish)', voiceSupported: true, voiceLocale: 'sv-SE', defaultVoice: 'Aoede' },
  { code: 'da', name: 'Dansk (Danish)', voiceSupported: true, voiceLocale: 'da-DK', defaultVoice: 'Aoede' },
  { code: 'fi', name: 'Suomi (Finnish)', voiceSupported: false, voiceLocale: 'fi-FI', defaultVoice: 'Aoede' },
  { code: 'nb', name: 'Norsk (Norwegian)', voiceSupported: true, voiceLocale: 'nb-NO', defaultVoice: 'Aoede' },
  { code: 'tr', name: 'Türkçe (Turkish)', voiceSupported: true, voiceLocale: 'tr-TR', defaultVoice: 'Aoede' },

  // Asian languages
  { code: 'ja', name: '日本語 (Japanese)', voiceSupported: true, voiceLocale: 'ja-JP', defaultVoice: 'Aoede' },
  { code: 'ko', name: '한국어 (Korean)', voiceSupported: true, voiceLocale: 'ko-KR', defaultVoice: 'Aoede' },
  { code: 'zh', name: '中文 (Mandarin)', voiceSupported: false, voiceLocale: 'cmn-CN', defaultVoice: 'Aoede' },
  { code: 'hi', name: 'हिन्दी (Hindi)', voiceSupported: true, voiceLocale: 'hi-IN', defaultVoice: 'Aoede' },
  { code: 'th', name: 'ไทย (Thai)', voiceSupported: true, voiceLocale: 'th-TH', defaultVoice: 'Aoede' },
  { code: 'vi', name: 'Tiếng Việt (Vietnamese)', voiceSupported: true, voiceLocale: 'vi-VN', defaultVoice: 'Aoede' },
  { code: 'id', name: 'Bahasa Indonesia (Indonesian)', voiceSupported: true, voiceLocale: 'id-ID', defaultVoice: 'Aoede' },

  // Eastern European languages
  { code: 'bg', name: 'Български (Bulgarian)', voiceSupported: false, voiceLocale: 'bg-BG', defaultVoice: 'Aoede' },
  { code: 'bs', name: 'Bosanski (Bosnian)', voiceSupported: false, voiceLocale: 'bs-BA', defaultVoice: 'Aoede' },
  { code: 'cs', name: 'Čeština (Czech)', voiceSupported: true, voiceLocale: 'cs-CZ', defaultVoice: 'Aoede' },
  { code: 'et', name: 'Eesti (Estonian)', voiceSupported: false, voiceLocale: 'et-EE', defaultVoice: 'Aoede' },
  { code: 'hr', name: 'Hrvatski (Croatian)', voiceSupported: false, voiceLocale: 'hr-HR', defaultVoice: 'Aoede' },
  { code: 'hu', name: 'Magyar (Hungarian)', voiceSupported: false, voiceLocale: 'hu-HU', defaultVoice: 'Aoede' },
  { code: 'lt', name: 'Lietuvių (Lithuanian)', voiceSupported: false, voiceLocale: 'lt-LT', defaultVoice: 'Aoede' },
  { code: 'lv', name: 'Latviešu (Latvian)', voiceSupported: false, voiceLocale: 'lv-LV', defaultVoice: 'Aoede' },
  { code: 'mk', name: 'Македонски (Macedonian)', voiceSupported: false, voiceLocale: 'mk-MK', defaultVoice: 'Aoede' },
  { code: 'ro', name: 'Română (Romanian)', voiceSupported: true, voiceLocale: 'ro-RO', defaultVoice: 'Aoede' },
  { code: 'sk', name: 'Slovenčina (Slovak)', voiceSupported: false, voiceLocale: 'sk-SK', defaultVoice: 'Aoede' },
  { code: 'sl', name: 'Slovenščina (Slovenian)', voiceSupported: false, voiceLocale: 'sl-SI', defaultVoice: 'Aoede' },
  { code: 'sq', name: 'Shqip (Albanian)', voiceSupported: false, voiceLocale: 'sq-AL', defaultVoice: 'Aoede' },
  { code: 'sr', name: 'Српски (Serbian)', voiceSupported: false, voiceLocale: 'sr-RS', defaultVoice: 'Aoede' },
  { code: 'be', name: 'Беларуская (Belarusian)', voiceSupported: false, voiceLocale: 'be-BY', defaultVoice: 'Aoede' },

  // Western European & Iberian languages
  { code: 'ca', name: 'Català (Catalan)', voiceSupported: false, voiceLocale: 'ca-ES', defaultVoice: 'Aoede' },
  { code: 'eu', name: 'Euskara (Basque)', voiceSupported: false, voiceLocale: 'eu-ES', defaultVoice: 'Aoede' },
  { code: 'gl', name: 'Galego (Galician)', voiceSupported: false, voiceLocale: 'gl-ES', defaultVoice: 'Aoede' },
  { code: 'el', name: 'Ελληνικά (Greek)', voiceSupported: true, voiceLocale: 'el-GR', defaultVoice: 'Aoede' },
  { code: 'is', name: 'Íslenska (Icelandic)', voiceSupported: false, voiceLocale: 'is-IS', defaultVoice: 'Aoede' },

  // South Asian languages
  { code: 'as', name: 'অসমীয়া (Assamese)', voiceSupported: false, voiceLocale: 'as-IN', defaultVoice: 'Aoede' },
  { code: 'bn', name: 'বাংলা (Bengali)', voiceSupported: true, voiceLocale: 'bn-IN', defaultVoice: 'Aoede' },
  { code: 'gu', name: 'ગુજરાતી (Gujarati)', voiceSupported: false, voiceLocale: 'gu-IN', defaultVoice: 'Aoede' },
  { code: 'kn', name: 'ಕನ್ನಡ (Kannada)', voiceSupported: false, voiceLocale: 'kn-IN', defaultVoice: 'Aoede' },
  { code: 'ml', name: 'മലയാളം (Malayalam)', voiceSupported: false, voiceLocale: 'ml-IN', defaultVoice: 'Aoede' },
  { code: 'mr', name: 'मराठी (Marathi)', voiceSupported: true, voiceLocale: 'mr-IN', defaultVoice: 'Aoede' },
  { code: 'ne', name: 'नेपाली (Nepali)', voiceSupported: false, voiceLocale: 'ne-NP', defaultVoice: 'Aoede' },
  { code: 'or', name: 'ଓଡ଼ିଆ (Odia)', voiceSupported: false, voiceLocale: 'or-IN', defaultVoice: 'Aoede' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)', voiceSupported: false, voiceLocale: 'pa-IN', defaultVoice: 'Aoede' },
  { code: 'ta', name: 'தமிழ் (Tamil)', voiceSupported: true, voiceLocale: 'ta-IN', defaultVoice: 'Aoede' },
  { code: 'te', name: 'తెలుగు (Telugu)', voiceSupported: true, voiceLocale: 'te-IN', defaultVoice: 'Aoede' },
  { code: 'ur', name: 'اردو (Urdu)', voiceSupported: false, voiceLocale: 'ur-PK', defaultVoice: 'Aoede' },

  // Southeast Asian languages
  { code: 'fil', name: 'Filipino', voiceSupported: false, voiceLocale: 'fil-PH', defaultVoice: 'Aoede' },
  { code: 'km', name: 'ខ្មែរ (Khmer)', voiceSupported: false, voiceLocale: 'km-KH', defaultVoice: 'Aoede' },
  { code: 'lo', name: 'ລາວ (Lao)', voiceSupported: false, voiceLocale: 'lo-LA', defaultVoice: 'Aoede' },
  { code: 'ms', name: 'Bahasa Melayu (Malay)', voiceSupported: false, voiceLocale: 'ms-MY', defaultVoice: 'Aoede' },

  // Central Asian & Caucasus languages
  { code: 'az', name: 'Azərbaycan (Azerbaijani)', voiceSupported: false, voiceLocale: 'az-AZ', defaultVoice: 'Aoede' },
  { code: 'hy', name: 'Հայերեն (Armenian)', voiceSupported: false, voiceLocale: 'hy-AM', defaultVoice: 'Aoede' },
  { code: 'ka', name: 'ქართული (Georgian)', voiceSupported: false, voiceLocale: 'ka-GE', defaultVoice: 'Aoede' },
  { code: 'kk', name: 'Қазақ (Kazakh)', voiceSupported: false, voiceLocale: 'kk-KZ', defaultVoice: 'Aoede' },
  { code: 'mn', name: 'Монгол (Mongolian)', voiceSupported: false, voiceLocale: 'mn-MN', defaultVoice: 'Aoede' },
  { code: 'uz', name: 'Oʻzbek (Uzbek)', voiceSupported: false, voiceLocale: 'uz-UZ', defaultVoice: 'Aoede' },

  // Middle Eastern & Semitic languages
  { code: 'ar', name: 'العربية (Arabic)', voiceSupported: true, voiceLocale: 'ar-XA', defaultVoice: 'Aoede' },
  { code: 'iw', name: 'עברית (Hebrew)', voiceSupported: false, voiceLocale: 'iw-IL', defaultVoice: 'Aoede' },

  // African languages
  { code: 'af', name: 'Afrikaans', voiceSupported: false, voiceLocale: 'af-ZA', defaultVoice: 'Aoede' },
  { code: 'am', name: 'አማርኛ (Amharic)', voiceSupported: false, voiceLocale: 'am-ET', defaultVoice: 'Aoede' },
  { code: 'sw', name: 'Kiswahili (Swahili)', voiceSupported: true, voiceLocale: 'sw-KE', defaultVoice: 'Aoede' },
  { code: 'zu', name: 'isiZulu (Zulu)', voiceSupported: true, voiceLocale: 'zu-ZA', defaultVoice: 'Aoede' },

  // African languages (added #1837, A3 Phase 1 — scoped to Yoma Navigator
  // surfaces for the wave; whole-app parity can follow). All nine were
  // added to NATIVE_AUDIO_VOICE_LOCALES 2026-09-16 (#1831 quality probe) —
  // voiceSupported is now true for all nine at the LANGUAGE level. The
  // Navigator scenarios that use these languages remain locked to
  // allowed_modality='text_only' at the SCENARIO level (migration 0362,
  // #1786) independent of this flag — that lock is unaffected by this
  // change and is #1791's to revisit, not this issue's.
  { code: 'yo', name: 'Yorùbá (Yoruba)', voiceSupported: true, voiceLocale: 'yo-NG', defaultVoice: 'Aoede' },
  { code: 'ha', name: 'Hausa', voiceSupported: true, voiceLocale: 'ha-NG', defaultVoice: 'Aoede' },
  { code: 'rw', name: 'Ikinyarwanda (Kinyarwanda)', voiceSupported: true, voiceLocale: 'rw-RW', defaultVoice: 'Aoede' },
  { code: 'so', name: 'Soomaali (Somali)', voiceSupported: true, voiceLocale: 'so-SO', defaultVoice: 'Aoede' },
  // om/wo/ak/tn: voiceSupported flipped to false 2026-09-22 (#3142,
  // D-154/D-156, WITHDRAWN_LANGUAGE_CODES above) — Tier C, withdrawn from
  // every surface, not only voice. voiceLocale kept (not nulled) since it
  // still describes the real Live API locale these codes would map to, for
  // any code still reading it directly rather than through voiceSupported.
  { code: 'om', name: 'Oromoo (Oromo)', voiceSupported: false, voiceLocale: 'om-ET', defaultVoice: 'Aoede' },
  { code: 'wo', name: 'Wolof', voiceSupported: false, voiceLocale: 'wo-SN', defaultVoice: 'Aoede' },
  { code: 'ak', name: 'Akan', voiceSupported: false, voiceLocale: 'ak-GH', defaultVoice: 'Aoede' },
  { code: 'tn', name: 'Setswana (Tswana)', voiceSupported: false, voiceLocale: 'tn-ZA', defaultVoice: 'Aoede' },
  // st/so KEPT true — D-156 (2026-09-17): Sesotho/Somali keep voice.
  { code: 'st', name: 'Sesotho (Southern Sotho)', voiceSupported: true, voiceLocale: 'st-ZA', defaultVoice: 'Aoede' },
];

/**
 * Grouped languages for dropdown display with <optgroup> headers.
 * Each group has a label and an array of language codes belonging to it.
 *
 * This is the RAW literal, per-region source list — every selector in the
 * app (LanguageSwitcher.jsx, ChatPage.jsx, HomePageV2.jsx, DemoPage.jsx,
 * ScenarioDetailPage.jsx) renders from the FILTERED `LANGUAGE_GROUPS` export
 * below, not this one directly, so a single filter here reaches every
 * dropdown without editing each call site (#3142).
 */
const RAW_LANGUAGE_GROUPS: { label: string; codes: string[] }[] = [
  { label: 'English', codes: ['en'] },
  { label: 'African', codes: ['af', 'am', 'fr', 'pt', 'sw', 'zu', 'yo', 'ha', 'rw', 'so', 'om', 'wo', 'ak', 'tn', 'st'] },
  { label: 'Western European', codes: ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'da', 'fi', 'nb', 'ca', 'eu', 'gl', 'el', 'is'] },
  { label: 'Eastern European', codes: ['bg', 'bs', 'cs', 'et', 'hr', 'hu', 'lt', 'lv', 'mk', 'ro', 'ru', 'sk', 'sl', 'sq', 'sr', 'uk', 'be'] },
  { label: 'East Asian', codes: ['ja', 'ko', 'zh'] },
  { label: 'South Asian', codes: ['hi', 'as', 'bn', 'gu', 'kn', 'ml', 'mr', 'ne', 'or', 'pa', 'ta', 'te', 'ur'] },
  { label: 'Southeast Asian', codes: ['th', 'vi', 'id', 'fil', 'km', 'lo', 'ms'] },
  { label: 'Central Asian & Caucasus', codes: ['az', 'hy', 'ka', 'kk', 'mn', 'tr', 'uz'] },
  { label: 'Middle Eastern', codes: ['ar', 'iw'] },
];

/**
 * `RAW_LANGUAGE_GROUPS` with every `WITHDRAWN_LANGUAGE_CODES` entry
 * filtered out of each group's `codes` (#3142). A withdrawn code that were
 * the ONLY member of some future group would leave an empty `codes: []`
 * array — `.filter(g => g.codes.length > 0)` drops such a group entirely
 * rather than rendering an empty `<optgroup>`; no group is empty today
 * (African drops from 15 to 11 codes; the other eight are unaffected).
 */
export const LANGUAGE_GROUPS: { label: string; codes: string[] }[] = RAW_LANGUAGE_GROUPS
  .map((group) => ({
    label: group.label,
    codes: group.codes.filter((code) => !WITHDRAWN_LANGUAGE_CODES.has(code)),
  }))
  .filter((group) => group.codes.length > 0);

/**
 * Map of language codes to their full names (for display)
 */
export const LANGUAGE_NAMES: Record<string, string> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map(lang => [lang.code, lang.name])
);

/**
 * Get language config by code
 */
export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

/**
 * Check if a language supports voice mode
 */
export function isVoiceSupported(code: string): boolean {
  const config = getLanguageConfig(code);
  return config?.voiceSupported ?? false;
}

/**
 * Get voice locale for a language (for Gemini Live API)
 */
export function getVoiceLocale(code: string): string | null {
  const config = getLanguageConfig(code);
  return config?.voiceLocale ?? null;
}

/**
 * Default language code
 */
export const DEFAULT_LANGUAGE = 'en';

const LANGUAGE_STORAGE_KEY = 'tacit-preferred-language';

/**
 * Get the user's preferred language from localStorage, falling back to default.
 *
 * #3357 (PR #3243 follow-on): a withdrawn code (`WITHDRAWN_LANGUAGE_CODES` —
 * `wo`/`ak`/`om`/`tn`, #3142 D-154/D-156) still has a `LANGUAGE_NAMES` entry
 * (existing data must still resolve a display name — #3142 "what must NOT
 * change"), so the old `LANGUAGE_NAMES[stored]` check alone let a learner
 * whose stored preference predates the withdrawal keep sending it. The
 * backend's own offered-language guard (`isLanguageOffered`,
 * backend/src/config/languages.js) then rejects it with 400 on every
 * `POST /api/runs` — this learner could never start a run until they
 * happened to change their language manually. Excluding withdrawn codes
 * here, the same way `LANGUAGE_GROUPS` already excludes them from every
 * selector, falls back to `DEFAULT_LANGUAGE` instead of round-tripping into
 * that 400 — mirroring the backend's own withdrawn-locale fallback
 * (runsv2.js's/coachingRoutes.js's `getOrgDefaultLanguage`, #3142).
 *
 * #3812: when reading storage THROWS (Safari "Block All Cookies", which needs
 * a Safari restart, so the page that shows the storage-blocked stop always
 * loads with storage already unavailable) the fallback is the first
 * `navigator.languages`/`navigator.language` entry that maps to an offered,
 * non-withdrawn code, then DEFAULT_LANGUAGE. Storage that works but holds no
 * preference still returns DEFAULT_LANGUAGE: following the browser language
 * for every new visitor is a product decision, not part of this fix.
 */
export function getPreferredLanguage(): string {
  let stored: string | null;
  try {
    stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // Storage is unavailable (blocked cookies, private browsing, SSR). A stored
    // preference cannot exist here, so the browser's own language is the only
    // signal left (#3812). Applies ONLY on a throw: when storage works but holds
    // no preference this stays DEFAULT_LANGUAGE.
    return getBrowserLanguage() ?? DEFAULT_LANGUAGE;
  }
  if (stored && LANGUAGE_NAMES[stored] && !WITHDRAWN_LANGUAGE_CODES.has(stored)) return stored;
  return DEFAULT_LANGUAGE;
}

/**
 * Legacy / alternate BCP-47 primary subtags -> the code this repo (and its
 * `public/locales/<code>/` directories) uses. Primary subtags that already
 * match a code ('fr', 'fil', 'pt', 'zh', 'nb') need no entry.
 */
const BROWSER_LANGUAGE_ALIASES: Readonly<Record<string, string>> = {
  he: 'iw', // Hebrew: browsers report 'he'; the locale directory is 'iw'
  no: 'nb', // Norwegian: browsers report 'no'; the locale directory is 'nb'
  tl: 'fil', // Tagalog: older browsers report 'tl'; the locale directory is 'fil'
};

/**
 * Map one BCP-47 tag ('fr-FR', 'zh-TW', 'he-IL', 'fil-PH', 'pt') to an OFFERED
 * code, or null. The primary subtag decides the language, so 'fr-FR' -> 'fr'
 * and 'zh-TW' -> 'zh'; the tag is lower-cased and '_' tolerated. A withdrawn
 * code (`WITHDRAWN_LANGUAGE_CODES`) is not offered: it would round-trip into
 * the backend's offered-language guard (#3357), and every offered code has a
 * `public/locales/<code>/` directory (asserted by the unit tests), so i18next
 * never requests a missing file.
 */
function mapBrowserTag(tag: unknown): string | null {
  if (typeof tag !== 'string') return null;
  const primary = tag.trim().toLowerCase().replace(/_/g, '-').split('-')[0];
  if (!primary) return null;
  const code = BROWSER_LANGUAGE_ALIASES[primary] ?? primary;
  // hasOwn, not `LANGUAGE_NAMES[code]`: a tag such as 'constructor' must not match.
  if (!Object.prototype.hasOwnProperty.call(LANGUAGE_NAMES, code)) return null;
  if (WITHDRAWN_LANGUAGE_CODES.has(code)) return null;
  return code;
}

/**
 * The first `navigator.languages` entry (then `navigator.language`) that maps
 * to an offered code, or null. `navigator` may be undefined (SSR, tests) and
 * the properties may be absent or malformed, so every read is guarded.
 */
function getBrowserLanguage(): string | null {
  try {
    if (typeof navigator === 'undefined' || !navigator) return null;
    const tags: unknown[] = Array.isArray(navigator.languages) ? [...navigator.languages] : [];
    tags.push(navigator.language);
    for (const tag of tags) {
      const code = mapBrowserTag(tag);
      if (code) return code;
    }
  } catch { /* hostile navigator: fall through to the default */ }
  return null;
}

/**
 * Save the user's preferred language to localStorage.
 */
export function setPreferredLanguage(code: string): void {
  try {
    if (code === DEFAULT_LANGUAGE) {
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    } else {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    }
  } catch { /* SSR or private browsing */ }
}
